import React, { memo } from 'react';
import { getAssetFromItem } from '../../assets/utils';
import { changeItemsWithSelection } from '../../state/actions/change-items';
import { updateItemHeight } from '../../state/actions/update-item-dimensions';
import {
  getKeepAspectRatio,
  getOriginalAspectRatio,
} from '../../utils/aspect-ratio';
import { getCropFromItem } from '../../utils/get-crop-from-item';
import { useWriteContext } from '../../utils/use-context';
import { useTargetItemIds } from '../utils/use-target-item-ids';
import { ToolButton } from '@crabs-ai/ds-react';
import { AspectRatio, AspectRatioFill } from '@crabs-ai/web-icons';

const KeepAspectRatioControlsUnmemoized = ({
  keepAspectRatio,
  itemId,
  isMixed = false,
}: {
  keepAspectRatio: boolean;
  itemId: string;
  isMixed?: boolean;
}) => {
  const { setState } = useWriteContext();
  const targetIds = useTargetItemIds(itemId);

  const onKeepAspectRatioChange = React.useCallback(() => {
    setState({
      update: (state) => {
        return changeItemsWithSelection(state, itemId, targetIds, (item) => {
          const newKeepAspectRatio = !getKeepAspectRatio(item);
          if (!newKeepAspectRatio) {
            return {
              ...item,
              keepAspectRatio: newKeepAspectRatio,
            } as typeof item;
          }

          // aspect ratio 활성화 - 원본 aspect ratio에 맞게 치수 조정
          const asset = getAssetFromItem({
            item: item,
            assets: state.undoableState.assets,
          });
          const aspectRatio = getOriginalAspectRatio({ item: item, asset });

          // crop을 고려하여 보이는 영역의 비율이 올바르게 유지되도록 height 계산
          const crop = getCropFromItem(item);
          const cropW = 1 - (crop?.cropLeft ?? 0) - (crop?.cropRight ?? 0);
          const cropH = 1 - (crop?.cropTop ?? 0) - (crop?.cropBottom ?? 0);
          // 보이는 영역 비율 = (item.width * cropW) / (item.height * cropH) = aspectRatio
          // 따라서 item.height = item.width * cropW / (aspectRatio * cropH)
          const newHeight = Math.round(
            (item.width * cropW) / (aspectRatio * cropH),
          );

          // keepAspectRatio와 height 모두 업데이트
          const updatedItem = updateItemHeight({
            item: {
              ...item,
              keepAspectRatio: newKeepAspectRatio,
            } as typeof item,
            height: newHeight,
            stopTextEditing: true,
          });
          return updatedItem;
        });
      },
      commitToUndoStack: true,
    });
  }, [setState, itemId, targetIds]);

  return (
    <div className="flex items-center gap-2xs">
      <ToolButton
        aria-label="Keep Aspect Ratio"
        state={!isMixed && keepAspectRatio ? 'selected' : 'default'}
        data-mixed={isMixed ? 'true' : undefined}
        onClick={onKeepAspectRatioChange}
      >
        {keepAspectRatio ? <AspectRatioFill /> : <AspectRatio />}
      </ToolButton>
      {isMixed && (
        <span className="text-2xs text-comp-moderate-medium">Mixed</span>
      )}
    </div>
  );
};

export const KeepAspectRatioControls = memo(KeepAspectRatioControlsUnmemoized);
